/**
 * Gemini journey — protocol `a2a-ap2` (ADR-0159 §3).
 *
 * Retains the AP2 IntentMandate → CartMandate → PaymentMandate storyline
 * (Google's AP2 protocol) with copy refreshed to the 2026-07-04 pass:
 * Google-Extended is the fetch UA; agentic checkout runs through UCP +
 * Google Pay on Google surfaces (waitlist-gated), AP2 mandates interop
 * with A2A and MCP. Role label is no longer "Project Astra".
 */

import { buildDiscoveryPhase, buildReadMenuPhase } from './discovery.js';
import type { JourneyContext, Phase } from './types.js';

export function buildGeminiJourney(ctx: JourneyContext): Phase[] {
  const { agent, live, installed, cardUrl, catalogUrl, liveProducts, cart } = ctx;
  return [
    buildDiscoveryPhase(agent, installed, cardUrl, live.card),
    buildReadMenuPhase(agent, installed, catalogUrl, live.catalog, liveProducts),
    {
      id: 'identity',
      name: 'Identity check',
      tier: 'illustration',
      narration:
        `Google publishes Google-Extended as a robots.txt control, but ${agent.name}'s fetch User-Agent is a generic browser string — useless for identity. Only a signed mandate proves who it is.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "verified": "illustration — signed handshake not performed here", "agent": "gemini" }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'intent',
      name: 'Intent mandate',
      tier: 'illustration',
      narration: `${agent.name}'s user confirmed "buy 2× ${cart.title}${cart.is_sample ? ' [sample]' : ''}." It issues a signed AP2 IntentMandate.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "mandate": "mnd_…", "decision": "approved" }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'cart',
      name: 'Cart',
      tier: 'illustration',
      narration: `It builds the cart: 2 × ${cart.title}${cart.is_sample ? ' [sample]' : ''}, plus tax & shipping.${cart.is_sample ? ' (Placeholder — your store has no products synced yet.)' : ''}`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: cart.unit_minor !== null
          ? `{ "mandate": "mnd_…", "total_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
          : '{ "mandate": "mnd_…", "total_minor": null }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'pay',
      name: 'Payment',
      tier: 'illustration',
      narration: `Documented by vendor: UCP + Google Pay handles agentic checkout on Google surfaces (waitlist-gated). Sill wraps the handoff in a signed AP2 PaymentMandate, checks it against your rules, and writes a signed audit record. (Illustration — no real charge here.)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: cart.unit_minor !== null
          ? `{ "decision": "approved", "settled": true, "amount_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
          : '{ "decision": "approved", "settled": true }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'fulfill',
      name: 'Fulfillment',
      tier: 'illustration',
      narration: `Order placed, tracking issued, customer emailed — every step would land in your signed audit log.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "fulfilled": true, "tracking": "1Z999AA1…" }',
        live: false,
      },
      ok: true,
    },
  ];
}
